import { Plugin, UpcastElementEvent } from "ckeditor5";
import { render } from "dom-serializer";
import { Document, Element, isTag, Text } from "domhandler";
import { appendChild } from "domutils";
import { parseDocument } from "htmlparser2";

const ANNOTATION_ENCODING = "application/vnd.wiris.mtweb-params+json";

export default class LegacyMath extends Plugin {
  init() {
    this.editor.conversion.for("upcast").add((dispatcher) => {
      dispatcher.on<UpcastElementEvent>(
        "element:math",
        (e, { modelRange }, { writer }) => {
          const modelItem = modelRange?.getContainedElement();
          if (modelItem == null) return;

          const fixed = this.fixCke4Math(
            modelItem?.getAttribute("formula") as string | undefined,
          );

          if (fixed == null) return;

          writer.setAttribute("formula", fixed, modelItem);
        },
        { priority: "lowest" },
      );
    });
  }

  private fixCke4Math(formula: string | undefined) {
    if (formula == null) return undefined;
    if (formula.includes(ANNOTATION_ENCODING)) return undefined;

    const config = this.editor.config.get("mathTypeParameters");

    const parsed = parseDocument(formula);

    const mathElement = parsed.children[0];
    if (!isTag(mathElement) || mathElement.name !== "math") return;

    const child = mathElement.children[0];
    if (!child || !isTag(child) || child.name === "semantics") return;

    //
    // If our math element does not have exactly one child of type <semantics>,
    // assume we are dealing with CKE4 math. We need to add an annotation to it to
    // tell Wiris how to render it.
    //
    // For example:
    //
    //  <math xmlns="http://www.w3.org/1998/Math/MathML">
    //    <msqrt><mn>5</mn></msqrt>
    //    <mo>+</mo>
    //    <msqrt><mn>5</mn></msqrt>
    //  </math>
    //
    // becomes:
    //
    //  <math xmlns="http://www.w3.org/1998/Math/MathML">
    //    <semantics>
    //      <mrow>
    //        <msqrt><mn>5</mn></msqrt>
    //        <mo>+</mo>
    //        <msqrt><mn>5</mn></msqrt>
    //      </mrow>
    //      <annotation encoding="application/vnd.wiris.mtweb-params+json">
    //        { <!-- wiris config JSON --> }
    //      </annotation>
    //    </semantics>
    //  </math>
    //

    if (mathElement.children.length > 1) {
      //
      // If there are multiple presentation elements, wrap them in an <mrow>.
      // The <semantics> element can only have a single presentation child, the first one
      //
      const rowElement = wrapChildren(mathElement, "mrow");
      appendChild(mathElement, rowElement);
    }

    const semanticsElement = wrapChildren(mathElement, "semantics");

    appendChild(
      semanticsElement,
      new Element("annotation", { encoding: ANNOTATION_ENCODING }, [
        new Text(JSON.stringify(config?.editorParameters ?? {})),
      ]),
    );

    return render(parsed);
  }
}

const wrapChildren = (
  node: Element | Document,
  name: string,
  attribs?: Record<string, string>,
) => {
  const wrapper = new Element(name, attribs ?? {});
  const children = Array.from(node.children);
  for (const child of children) {
    appendChild(wrapper, child);
  }
  appendChild(node, wrapper);
  return wrapper;
};
